import { useEffect, useState } from 'react';
import type { SingularSDK } from 'singular-sdk';

import { useIsSegmentLoaded } from 'helpers';

let singularSdk: SingularSDK | null = null;
let pendingLoginUserId: string | null = null;

export const initSingular = async () => {
  const sdkKey = process.env.GATSBY_SINGULAR_SDK_KEY;
  const sdkSecret = process.env.GATSBY_SINGULAR_SDK_SECRET;
  const productId = process.env.GATSBY_SINGULAR_PRODUCT_ID;
  const persistentDomain = process.env.GATSBY_SINGULAR_PERSISTENT_DOMAIN;

  if (!sdkKey || !sdkSecret || !productId) {
    return;
  }

  try {
    const { singularSdk: sdk, SingularConfig } = await import('singular-sdk');

    const config = new SingularConfig(sdkKey, sdkSecret, productId);

    if (persistentDomain) {
      // Uses the same domain as on Web to enable cross-subdomain tracking
      // https://support.singular.net/hc/en-us/articles/41863502734619-Web-SDK-Native-JavaScript-Implementation-Guide#Cross-subdomain_Tracking
      config.withAutoPersistentSingularDeviceId(persistentDomain);
    }

    sdk.init(config);
    singularSdk = sdk;

    // if useSingularIdentify was called before initialization, login in the user now
    if (pendingLoginUserId) {
      sdk.login(pendingLoginUserId);
      pendingLoginUserId = null;
    }

    sdk.pageVisit();
  } catch (error) {
    console.warn('Singular initialization failed:', error);
  }
};

export const singular = {
  pageVisit: () => {
    singularSdk?.pageVisit();
  },
  event: (eventName: string) => {
    singularSdk?.event(eventName);
  },
  eventWithArgs: (eventName: string, args: Record<string, unknown>) => {
    singularSdk?.eventWithArgs(eventName, args);
  },
  login: (userId: string) => {
    if (singularSdk) {
      singularSdk.login(userId);
    } else {
      pendingLoginUserId = userId;
    }
  },
};

export const useSingularIdentify = () => {
  const isSegmentLoaded = useIsSegmentLoaded();
  const [identified, setIdentified] = useState(false);

  useEffect(() => {
    if (isSegmentLoaded && !identified) {
      try {
        const anonymousId = window.analytics.user().anonymousId();
        if (anonymousId) {
          singular.login(anonymousId);
          setIdentified(true);
        }
      } catch (err) {
        console.warn('Failed to identify user with Singular:', err);
      }
    }
  }, [isSegmentLoaded, identified]);
};
